/**
 * /dashboard/accounting/reports/balance-sheet — Balance Sheet (G_ACC8).
 *
 * As-of date picker, Schedule III Part I grouped layout, §128(1)
 * balanced-check alert, CSV export.
 *
 * Shareholders' Funds carries COMPUTED lines (BSLine.kind) for the
 * profit / (loss) not yet transferred to Retained Earnings — the current
 * financial year's (1 April → as of) and any earlier years' — so the
 * statement balances on every date without a year-end closing entry.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/balance-sheet/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatAccountsAmount as formatPaise } from "@/lib/accounting-gst";
import { LedgerLink } from "@/components/accounting/ledger-link";
import { formatDate } from "@/lib/format";
import { balanceSheetCSVURL } from "@/lib/api/accounting";
import { useBalanceSheetReport } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";
import type { BSLineGroup } from "@/types/accounting";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/balance-sheet",
)({
  component: BalanceSheetRoute,
});

function todayUTCDate(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function BSGroup({ group, asOf }: Readonly<{ group: BSLineGroup; asOf: string }>) {
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
        {group.label}
      </div>
      <div className="divide-y">
        {group.lines.length === 0 ? (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            No accounts under this head.
          </div>
        ) : (
          group.lines.map((l) => (
            <div
              key={l.kind ?? l.account_id}
              className="grid grid-cols-[5rem_1fr_auto] gap-3 px-4 py-2 text-sm"
              data-testid={l.kind ? `bs-line-${l.kind}` : undefined}
            >
              <span className="font-mono text-xs text-muted-foreground">
                {l.kind ? "—" : l.account_code}
              </span>
              <span>
                {l.account_name}
                {l.kind && (
                  <span className="block text-xs text-muted-foreground">
                    Computed from your income and expense accounts — no
                    year-end entry has moved it to Retained Earnings yet.
                  </span>
                )}
              </span>
              <span className="tabular-nums">
                {l.kind ? (
                  formatPaise(l.amount_minor)
                ) : (
                  <LedgerLink accountId={l.account_id} to={asOf} label={`Ledger of ${l.account_name}`}>
                    {formatPaise(l.amount_minor)}
                  </LedgerLink>
                )}
              </span>
            </div>
          ))
        )}
        <div className="grid grid-cols-[5rem_1fr_auto] gap-3 bg-muted/20 px-4 py-2 text-sm font-medium">
          <span />
          <span>{group.label} — Subtotal</span>
          <span className="tabular-nums">
            {formatPaise(group.subtotal_minor)}
          </span>
        </div>
      </div>
    </div>
  );
}

function BalanceSheetRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [asOf, setAsOf] = useState<string>(todayUTCDate());
  const [pending, setPending] = useState<string>(asOf);
  const query = useBalanceSheetReport(asOf);
  const data = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="Balance Sheet"
      description="Companies Act 2013 Schedule III Part I — equity, liabilities, and assets."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={balanceSheetCSVURL(asOf)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="bs-asof">As Of</Label>
            <Input
              id="bs-asof"
              type="date"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => setAsOf(pending)}
              disabled={!pending || pending === asOf}
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load balance sheet</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-6">
          {!data.balanced && (
            <Alert variant="destructive">
              <AlertTitle>Balance Sheet does not balance</AlertTitle>
              <AlertDescription>
                Total Equity and Liabilities{" "}
                {formatPaise(data.total_equity_liabilities_minor)} does not
                equal Total Assets {formatPaise(data.total_assets_minor)}.
                This year&apos;s profit is already included above, so a gap
                points to a problem in the books (for example an account with
                an unexpected type). Ask your accountant to check the{" "}
                <Link
                  to="/app/$tenantSlug/accounting/reports/trial-balance"
                  params={{ tenantSlug }}
                  className="font-medium underline underline-offset-2"
                >
                  Trial Balance
                </Link>{" "}
                (Companies Act §128(1) requires a true and fair view).
              </AlertDescription>
            </Alert>
          )}

          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              I. Equity and Liabilities
            </h2>
            <BSGroup group={data.equity} asOf={data.as_of} />
            <BSGroup group={data.non_current_liabilities} asOf={data.as_of} />
            <BSGroup group={data.current_liabilities} asOf={data.as_of} />
            <Card>
              <CardContent className="flex justify-between py-3 text-sm font-medium">
                <span>Total Equity and Liabilities</span>
                <span className="tabular-nums">
                  {formatPaise(data.total_equity_liabilities_minor)}
                </span>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">II. Assets</h2>
            <BSGroup group={data.non_current_assets} asOf={data.as_of} />
            <BSGroup group={data.current_assets} asOf={data.as_of} />
            <Card>
              <CardContent className="flex justify-between py-3 text-sm font-medium">
                <span>Total Assets</span>
                <span className="tabular-nums">
                  {formatPaise(data.total_assets_minor)}
                </span>
              </CardContent>
            </Card>
          </section>

          <p className="text-xs text-muted-foreground">
            {data.balanced ? "Balanced" : "Unbalanced"} as of {formatDate(data.as_of)}.
          </p>
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
